import React, { useRef, useEffect, useMemo, useState } from 'react'
import { useChat, type ChatEntry, DISPLAY_PAIR_LIMIT } from '@/hooks/useChat'
import { useRole } from '@/hooks/useRole'
import { useTodayCalendar } from '@/hooks/useTodayCalendar'
import { useFileAttachment } from '@/hooks/useFileAttachment'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useBranding } from '@/contexts/BrandingContext'
import { Spinner } from '@/components/Spinner'
import { ChatHeader } from './ChatHeader'
import { InfoPanel } from './InfoPanel'
import { MessageInput } from './MessageInput'
import { MessageBubble } from './MessageBubble'
import { VoiceNotesBar } from './VoiceNotesBar'
import { buildTranscriptHandoff, loadStoredNoteStyleId, storeNoteStyleId, type NoteCustomer } from '@/lib/voiceNotes'
import { appendMentionContext, type MentionedCustomer } from '@/lib/mentions'
import type { AttachedFile } from '@/lib/api'
import type { CustomerConfirmCandidate } from '@/lib/render'

// Replaces FlatList's scrollToEnd({ animated: true }).
function scrollToEnd(el: HTMLDivElement | null) {
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
}

interface ChatInterfaceProps {
  title?: string
  placeholder?: string
  onHeaderAction?: () => void
  headerLeft?: React.ReactNode
  hideAiAvatar?: boolean
  // Customer mode: persist + poll the conversation thread, show who is responding,
  // surface the "a team member is helping" banner, and enable /progress logging.
  customerThread?: boolean
  // Operator mode: seed the chat with today's schedule as the first message,
  // fetched statically from the API (no AI round-trip).
  showTodayCalendar?: boolean
  timezone?: string
  // Pinned content rendered between the header and the message list (e.g. the
  // customer's upcoming appointments). Stays visible as the thread grows.
  topAccessory?: React.ReactNode
  // Sent once, automatically, on mount. Used by the calendar's pay action to
  // hand a booking the AI needs to decide on straight into the conversation.
  // The caller must clear whatever it read this from BEFORE rendering, so a
  // page refresh cannot replay the question.
  autoAsk?: string
  // Operator mode: voice-notes recorder + note-style picker above the composer.
  // The selected style's id rides with every send (v1-chat injects its
  // summarisation instructions server-side).
  operatorNoteTools?: boolean
}

export function ChatInterface({
  title = 'AI Assistant',
  placeholder,
  onHeaderAction,
  headerLeft,
  hideAiAvatar,
  customerThread,
  showTodayCalendar,
  timezone,
  topAccessory,
  autoAsk,
  operatorNoteTools,
}: ChatInterfaceProps) {
  const branding = useBranding()
  const { userRole } = useRole()
  const todayCalendar = useTodayCalendar(timezone, !!showTodayCalendar)
  const introEntry = useMemo<ChatEntry | null>(() => {
    if (!showTodayCalendar || !todayCalendar.props) return null
    const hasEvents = todayCalendar.props.events.length > 0
    return {
      id: 'today-calendar',
      role: 'assistant',
      // Only render the calendar when there's something to show — otherwise say so
      // in plain text instead of an empty calendar frame.
      parts: hasEvents
        ? [{ kind: 'render', component: 'calendar', props: todayCalendar.props, caption: 'Today’s schedule' }]
        : [{ kind: 'text', content: 'No appointments scheduled for today.' }],
      timestamp: new Date(),
    }
  }, [showTodayCalendar, todayCalendar.props])

  // WHO this conversation is about — general context, not note-specific: every
  // customer-scoped ask (bookings, payments, notes, attachments) refers to this
  // person until the operator names another. Deliberately not persisted — a
  // stale pin silently attributing today's work to yesterday's customer is the
  // exact mis-attribution bug this feature exists to prevent.
  const [noteCustomer, setNoteCustomer] = useState<NoteCustomer | null>(null)

  // Mirrors the pin so applyNoteCustomer can compare against the current value
  // without a state updater — announcing inside one would emit the line twice
  // under StrictMode, which double-invokes updaters.
  const noteCustomerRef = useRef<NoteCustomer | null>(null)

  // The pin and the AI's own customer search are two views of ONE decision about
  // who this conversation is about, so every path that resolves a customer comes
  // through here: the composer picker, a customer_confirm button, and v1-chat's
  // resolved_customer echo. The system line is what makes the link visible —
  // without it a pin that moved on its own reads as the wrong customer.
  const applyNoteCustomer = (next: NoteCustomer | null) => {
    if (noteCustomerRef.current?.id === next?.id) return
    noteCustomerRef.current = next
    setNoteCustomer(next)
    addSystemNote(
      next
        ? `📌 Now working with ${next.name} — chat refers to them until you unpin.`
        : '📌 Customer unpinned — name or @-mention who you mean next.',
    )
  }

  const { entries, isLoading, handler, sendMessage, addSystemNote, clearHistory } = useChat({
    customerThread,
    introEntry,
    // Only the operator surface has a pin to move.
    onCustomerResolved: operatorNoteTools
      ? c => applyNoteCustomer({ id: c.id, name: c.name ?? 'Unnamed customer' })
      : undefined,
  })
  const [inputText, setInputText] = useState('')
  const [showOlder, setShowOlder] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const isOnline = useOnlineStatus()

  const {
    pendingFiles,
    fileError,
    isDragging,
    handleAttachPress,
    handleCameraPress,
    addAttachedFiles,
    removePendingFile,
    clearPendingFiles,
    clearFileError,
  } = useFileAttachment()

  const [noteStyleId, setNoteStyleId] = useState<string | null>(() => loadStoredNoteStyleId())
  // Customers the operator @-mentioned while composing. Their ids are appended
  // to the message text on send (see handleSend) because chat history is
  // text-only — an id that never reaches the transcript is one the model
  // invents on a later turn. Kept per composer session, pruned at send time to
  // whoever is still named in the text.
  //
  // noteCustomer is NOT re-declared here: this branch moved it above the useChat
  // call, because useChat's onCustomerResolved option needs applyNoteCustomer.
  const [mentioned, setMentioned] = useState<MentionedCustomer[]>([])

  const handleNoteStyleChange = (id: string | null) => {
    setNoteStyleId(id)
    storeNoteStyleId(id)
  }

  // The transcript arrives as a ready-made text attachment; prefill a handoff
  // that says what is attached, what should happen, and — when pinned — WHO it
  // is about, so "attach → send" works without extra typing.
  const handleAttachTranscript = (file: AttachedFile) => {
    addAttachedFiles([file])
    setInputText(t => (t.trim() ? t : buildTranscriptHandoff(noteCustomer)))
  }

  // Auto-scroll when entries change
  useEffect(() => {
    if (entries.length > 0) {
      setTimeout(() => scrollToEnd(scrollRef.current), 100)
    }
  }, [entries.length])

  // Replaces FlatList's onContentSizeChange — stay pinned to the bottom as the
  // content itself grows (typing indicator appearing, a render part mounting).
  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    const observer = new ResizeObserver(() => scrollToEnd(scrollRef.current))
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  const visibleCount = DISPLAY_PAIR_LIMIT * 2
  const hasOlderEntries = entries.length > visibleCount
  const displayedEntries =
    hasOlderEntries && !showOlder ? entries.slice(entries.length - visibleCount) : entries

  const handleSend = () => {
    const trimmed = inputText.trim()
    if (!trimmed || isLoading) return
    // Mentioned customers' ids ride in the message TEXT; the bubble hides that
    // line when it displays the message, so the operator never reads a uuid.
    const text = appendMentionContext(trimmed, mentioned)
    setInputText('')
    setMentioned([])
    const files = pendingFiles
    clearPendingFiles()
    clearFileError()
    sendMessage(
      text,
      files,
      operatorNoteTools ? noteStyleId ?? undefined : undefined,
      operatorNoteTools ? noteCustomer?.id : undefined,
    )
  }

  // Quick actions on a rendered calendar are an OPERATOR affordance. Gated on
  // the resolved role, not on which page mounted this component: CustomerChat
  // and TenantChat render the same ChatInterface, and "mark paid" appearing in
  // a customer's thread would be a live data-integrity hole, not a cosmetic bug.
  // customerThread mode is excluded outright — that view is the customer's own
  // conversation even when an owner is the one signed in.
  const operatorSurface = !customerThread && (userRole === 'owner' || userRole === 'staff')

  // Fire the handed-off question exactly once. The ref (not state) is what makes
  // it once — React 19 StrictMode double-invokes effects in development, and a
  // state guard would still let the first send through twice.
  const autoAskSent = useRef(false)
  useEffect(() => {
    if (!autoAsk || autoAskSent.current || isLoading) return
    autoAskSent.current = true
    sendMessage(autoAsk)
  }, [autoAsk, isLoading, sendMessage])

  // Button-driven renderers (customer_confirm) send the user's choice as a
  // normal chat message — equivalent to typing it, so no extra gating beyond
  // the same not-while-loading rule the composer has.
  const handleQuickReply = (text: string) => {
    if (isLoading) return
    sendMessage(text)
  }

  // Confirming a customer in chat pins them too, so the operator never answers
  // "which customer" twice. Gated on the note tools being present — the pin only
  // exists on the operator surface.
  const handleCustomerPick = operatorNoteTools
    ? (customer: CustomerConfirmCandidate) =>
        applyNoteCustomer({ id: customer.id, name: customer.name })
    : undefined

  // /progress shortcut: prefill the command and open the photo picker.
  const handleLogProgress = () => {
    setInputText(t => (t.trim().toLowerCase().startsWith('/progress') ? t : '/progress '))
    handleCameraPress()
  }

  // A new chat is a new subject: carrying yesterday's pin into an empty thread is
  // the same mis-attribution the pin is session-scoped to avoid, and the line
  // that announced it has just been cleared away.
  const handleNewChat = () => {
    noteCustomerRef.current = null
    setNoteCustomer(null)
    clearHistory()
  }

  const showHumanBanner = customerThread && handler === 'human'

  const ListHeader = () => {
    if (!hasOlderEntries || showOlder) return null
    const hiddenCount = entries.length - visibleCount
    return (
      <button type="button" className="mb-2 w-full py-2.5 text-center" onClick={() => setShowOlder(true)}>
        <span className="text-xs italic text-muted-foreground/70">
          {'↑'} {hiddenCount} older {hiddenCount === 1 ? 'message' : 'messages'} — tap to show
        </span>
      </button>
    )
  }

  const ListFooter = () => {
    if (!isLoading) return null
    return (
      <div className="flex items-center gap-2 px-4 py-2" style={{ color: branding.primaryColor }}>
        <Spinner size={20} className="text-current" />
        <span className="text-[13px] italic text-muted-foreground/70">Thinking…</span>
      </div>
    )
  }

  const ListEmpty = () => {
    if (showTodayCalendar && todayCalendar.loading) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center px-8 py-20">
          <span className="inline-flex" style={{ color: branding.primaryColor }}>
            <Spinner size={20} className="text-current" />
          </span>
          <p className="mt-2.5 text-sm text-muted-foreground/70">Loading today’s schedule…</p>
        </div>
      )
    }
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8 py-20">
        <p className="mb-2 text-center text-xl font-bold text-foreground">How can I help?</p>
        <p className="text-center text-sm leading-5 text-muted-foreground/70">
          {operatorSurface
            ? 'Ask or tell me anything you want to do with customers, bookings, services, or packages.'
            : 'Ask me to book, check, or move your appointments — or about services and packages.'}
        </p>
      </div>
    )
  }

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col bg-muted">
      {/* Drag-and-drop overlay */}
      {isDragging && (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center border-2 border-dashed bg-slate-900/[0.04]"
          style={{ borderColor: branding.primaryColor }}
        >
          <span className="mb-2 text-4xl">{String.fromCodePoint(0x1f4ce)}</span>
          <span className="text-base font-semibold" style={{ color: branding.primaryColor }}>
            Drop to attach
          </span>
        </div>
      )}

      <ChatHeader
        title={title}
        isLoading={isLoading}
        isOnline={isOnline}
        headerLeft={headerLeft}
        hideAiAvatar={hideAiAvatar}
        onNewChat={onHeaderAction ?? handleNewChat}
        showInfo={showInfo}
        onToggleInfo={() => setShowInfo(v => !v)}
      />

      {showHumanBanner && (
        <div className="border-b border-teal-200 bg-teal-50 px-4 py-2">
          <p className="text-[13px] font-medium text-teal-700">
            👤 A team member is helping you — replies may take a little longer than usual.
          </p>
        </div>
      )}

      {topAccessory}

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div ref={contentRef} className="flex min-h-full flex-col gap-3 p-4">
          <ListHeader />
          {displayedEntries.length === 0 ? (
            <ListEmpty />
          ) : (
            displayedEntries.map(item => (
              <MessageBubble
                key={item.id}
                entry={item}
                interactive={operatorSurface}
                onQuickReply={handleQuickReply}
                onCustomerPick={handleCustomerPick}
              />
            ))
          )}
          <ListFooter />
        </div>
      </div>

      <InfoPanel
        visible={showInfo}
        audience={operatorSurface ? 'operator' : 'customer'}
        staffNote={operatorSurface && userRole === 'staff'}
        onClose={() => setShowInfo(false)}
      />

      {operatorNoteTools && (
        <VoiceNotesBar
          disabled={isLoading}
          noteStyleId={noteStyleId}
          onNoteStyleChange={handleNoteStyleChange}
          noteCustomer={noteCustomer}
          onNoteCustomerChange={applyNoteCustomer}
          onAttachTranscript={handleAttachTranscript}
        />
      )}

      {customerThread && (
        <div className="flex bg-card px-3 pt-2">
          <button
            type="button"
            className="rounded-2xl border border-blue-200 bg-blue-50 px-3 py-1.5"
            onClick={handleLogProgress}
            disabled={isLoading}
          >
            <span className="text-[13px] font-medium text-blue-700">📸 Log progress</span>
          </button>
        </div>
      )}

      <MessageInput
        value={inputText}
        onChange={setInputText}
        onSend={handleSend}
        isLoading={isLoading}
        placeholder={placeholder}
        pendingFiles={pendingFiles}
        fileError={fileError}
        onRemoveFile={removePendingFile}
        onAttachFile={handleAttachPress}
        onTakePhoto={handleCameraPress}
        // Same gate as the calendar quick actions: mentions search the whole
        // customer base, so they belong to operators only — never to a customer
        // typing in their own thread.
        enableMentions={operatorSurface}
        onMention={(customer) =>
          setMentioned((prev) => (prev.some((m) => m.id === customer.id) ? prev : [...prev, customer]))
        }
      />
    </div>
  )
}
